import React, { useEffect, useState } from "react";
import {
  Grid,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Card,
  CardHeader,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Stack,
  TextField,
  MenuItem,
  Typography,
  Chip,
} from "@mui/material";
import { CalendarMonth, Close, CurrencyRupee } from "@mui/icons-material";
import { dashboardAPI } from "../../services/api";

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
  }).format(value || 0);
};

export default function StudentOverviewTable() {
  const [studentData, setStudentData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [feeDuesOpen, setFeeDuesOpen] = useState(false);
  const [feeDuesData, setFeeDuesData] = useState(null);
  const [feeDuesLoading, setFeeDuesLoading] = useState(false);
  const [selectedClass, setSelectedClass] = useState('');
  const [classFilter, setClassFilter] = useState('');

  useEffect(() => {
    loadStudentData();
  }, []);

  const loadStudentData = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await dashboardAPI.getAdminStats();
      if (response.data?.classWiseCount) {
        setStudentData(response.data.classWiseCount);
      }
    } catch (err) {
      console.error('Failed to load student data:', err);
      setError('Failed to load student overview');
    } finally {
      setLoading(false);
    }
  };

  const loadFeeDuesData = async (standard = '') => {
    try {
      setFeeDuesLoading(true);
      setSelectedClass(standard);
      
      let url = '/dashboard.php?action=fee-dues';
      if (standard) {
        url += `&standard=${encodeURIComponent(standard)}`;
      }
      
      const response = await fetch(
        process.env.REACT_APP_API_BASE_URL + url,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (!response.ok) throw new Error('Failed to fetch fee dues');
      const data = await response.json();
      setFeeDuesData(data);
      setFeeDuesOpen(true);
    } catch (err) {
      console.error('Failed to load fee dues:', err);
      setError('Failed to load fee dues data');
    } finally {
      setFeeDuesLoading(false);
    }
  };

  const dataWithTotals = studentData.map((row) => ({
    ...row,
    total: row.total || 0,
    boys: row.boys || 0,
    girls: row.girls || 0,
  }));

  const totals = dataWithTotals.reduce(
    (acc, row) => {
      acc.boys += row.boys;
      acc.girls += row.girls;
      acc.total += row.total;
      return acc;
    },
    { boys: 0, girls: 0, total: 0 }
  );

  const getUniqueClasses = () => {
    return [...new Set(studentData.map(s => s.standard))].sort();
  };

  const filteredFeeDuesStudents = feeDuesData?.students?.filter(s => 
    !classFilter || s.standard === classFilter
  ) || [];

  return (
    <>
      <Grid item xs={12} lg={12}>
        <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
          {/* Header */}
          <CardHeader
            title="Student Overview"
            action={
              <Button
                size="small"
                endIcon={<CalendarMonth />}
                sx={{ textTransform: "none", fontWeight: 600 }}
                onClick={loadStudentData}
              >
                Refresh
              </Button>
            }
          />

          {/* Content */}
          <CardContent>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Alert severity="error">{error}</Alert>
            ) : (
              <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: "none" }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                      <TableCell><b>Class</b></TableCell>
                      <TableCell align="center"><b>Boys</b></TableCell>
                      <TableCell align="center"><b>Girls</b></TableCell>
                      <TableCell align="center"><b>Total</b></TableCell>
                      <TableCell align="center"><b>Action</b></TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {dataWithTotals.map((row, index) => (
                      <TableRow key={index} hover>
                        <TableCell><b>{row.standard}</b></TableCell>
                        <TableCell align="center" sx={{ color: "#2563eb", fontWeight: 600 }}>
                          {row.boys}
                        </TableCell>
                        <TableCell align="center" sx={{ color: "#ec4899", fontWeight: 600 }}>
                          {row.girls}
                        </TableCell>
                        <TableCell align="center">
                          <b>{row.total}</b>
                        </TableCell>
                        <TableCell align="center">
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            startIcon={<CurrencyRupee />}
                            onClick={() => loadFeeDuesData(row.standard)}
                            sx={{ textTransform: 'none', fontWeight: 600 }}
                          >
                            View Dues
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}

                    {/* Total Row */}
                    <TableRow sx={{ backgroundColor: "#f1f5f9" }}>
                      <TableCell><b>Total</b></TableCell>
                      <TableCell align="center"><b>{totals.boys}</b></TableCell>
                      <TableCell align="center"><b>{totals.girls}</b></TableCell>
                      <TableCell align="center"><b>{totals.total}</b></TableCell>
                      <TableCell align="center">
                        <Button
                          size="small"
                          variant="contained"
                          color="error"
                          startIcon={<CurrencyRupee />}
                          onClick={() => loadFeeDuesData('')}
                          sx={{ textTransform: 'none', fontWeight: 600 }}
                        >
                          All Dues
                        </Button>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* Fee Dues Modal */}
      <Dialog open={feeDuesOpen} onClose={() => setFeeDuesOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h6">
              📋 Students with Fee Dues
            </Typography>
            {selectedClass && (
              <Typography variant="body2" color="text.secondary">
                Class: <Chip label={selectedClass} size="small" sx={{ ml: 1 }} />
              </Typography>
            )}
          </Box>
          <Button
            onClick={() => setFeeDuesOpen(false)}
            sx={{ color: 'gray' }}
          >
            <Close />
          </Button>
        </DialogTitle>

        <DialogContent dividers>
          {feeDuesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : !feeDuesData ? (
            <Alert severity="error">No data available</Alert>
          ) : (
            <>
              {/* Summary */}
              <Stack spacing={2} sx={{ mb: 3 }}>
                <Box sx={{ p: 2, backgroundColor: '#f0f8ff', borderRadius: 1, border: '1px solid #ddd' }}>
                  <Typography variant="h6" fontWeight="bold">📊 Summary</Typography>
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Students with Dues</Typography>
                        <Typography variant="h5" fontWeight="bold" color="error">
                          {feeDuesData.summary.total_students_with_dues}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Total Fees Assigned</Typography>
                        <Typography variant="h5" fontWeight="bold">
                          {formatCurrency(feeDuesData.summary.total_fees_amount)}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Total Paid</Typography>
                        <Typography variant="h5" fontWeight="bold" color="success.main">
                          {formatCurrency(feeDuesData.summary.total_paid_amount)}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Total Due</Typography>
                        <Typography variant="h5" fontWeight="bold" color="error">
                          {formatCurrency(feeDuesData.summary.total_fees_due)}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>

                {/* Class Filter */}
                {getUniqueClasses().length > 1 && (
                  <Box>
                    <TextField
                      select
                      label="Filter by Class"
                      value={classFilter}
                      onChange={(e) => setClassFilter(e.target.value)}
                      size="small"
                      sx={{ minWidth: 200 }}
                    >
                      <MenuItem value="">All Classes</MenuItem>
                      {getUniqueClasses().map(cls => (
                        <MenuItem key={cls} value={cls}>{cls}</MenuItem>
                      ))}
                    </TextField>
                  </Box>
                )}
              </Stack>

              {/* Students Table */}
              <TableContainer sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                      <TableCell><b>Enrollment No.</b></TableCell>
                      <TableCell><b>Student Name</b></TableCell>
                      <TableCell><b>Class</b></TableCell>
                      <TableCell align="right"><b>Total Fees</b></TableCell>
                      <TableCell align="right"><b>Paid</b></TableCell>
                      <TableCell align="right"><b>Balance Due</b></TableCell>
                      <TableCell><b>Parent</b></TableCell>
                      <TableCell><b>Phone</b></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredFeeDuesStudents.length > 0 ? (
                      filteredFeeDuesStudents.map((student) => (
                        <TableRow key={student.id} hover>
                          <TableCell>{student.enrollment_no || '-'}</TableCell>
                          <TableCell>
                            <Typography fontWeight={600}>{student.student_name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {student.gender}
                            </Typography>
                          </TableCell>
                          <TableCell><Chip label={student.standard} size="small" /></TableCell>
                          <TableCell align="right">{formatCurrency(student.total_fees)}</TableCell>
                          <TableCell align="right" sx={{ color: 'green', fontWeight: 600 }}>
                            {formatCurrency(student.paid_amount)}
                          </TableCell>
                          <TableCell align="right" sx={{ color: 'error.main', fontWeight: 600 }}>
                            {formatCurrency(student.balance_due)}
                          </TableCell>
                          <TableCell>{student.father_name || '-'}</TableCell>
                          <TableCell>{student.parent_phone || '-'}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                          <Typography color="text.secondary">
                            No students with dues in this class
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setFeeDuesOpen(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}